import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Collection, Owner, CollectionEventType } from '@amzn/awscat-pattern-library-service-model';
import {
  Button,
  Pagination,
  PropertyFilter,
  SpaceBetween,
  StatusIndicator,
  Table,
  Modal,
  Select,
  TableProps,
  CollectionPreferencesProps,
  Header,
  Link,
} from '@amzn/awsui-components-core';
import { AppContext } from '../../contexts/AppContext';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { DateTime } from 'luxon';

import { IMplServiceClient } from '../../api/IMplServiceClient';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import { TableEmptyState, TableNoMatchState } from '../common/CommonComponents';
import constants from '../../common/constants';
import { collectionAudienceLabelMap, CollectionOwners, EditCollectionOwners } from '../collection';
import { COLLECTION_VIEWER_PREFIX } from '../../common/RouterPaths';
import { COLLECTION_FILTERING_PROPERTIES } from './utils';
import {
  DefaultI18nStrings,
  getFilterCounterText,
} from '../workflow-viewer/ContentWorkflowTableFilters';
import { Preferences } from '../common/CommonComponents';
import { tablePaginationAriaLabels } from '../../api/UtilsApi';

interface AdminCollectionTableItem extends Collection {
  ownerNamesString?: string;
}
const setOfficialOptions = [
  {
    label: constants.ADMIN_COLLECTION_COL_OFFICIAL_OFFERING_ID,
    value: constants.ADMIN_COLLECTION_COL_OFFICIAL_OFFERING_ID,
  },
  {
    label: constants.ADMIN_COLLECTION_COL_UNOFFICIAL_OFFERING_ID,
    value: constants.ADMIN_COLLECTION_COL_UNOFFICIAL_OFFERING_ID,
  },
];

const mplServiceClient: IMplServiceClient = MplServiceClientFactory.create();

const AdminCollectionsTable = () => {
  const appContext = useContext(AppContext);
  const userProfile = appContext?.authnInfo?.userProfile;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [collections, setCollections] = useState<AdminCollectionTableItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<AdminCollectionTableItem>();
  const [showEditOwnersModal, setShowEditOwnersModal] = useState<boolean>(false);
  const [isUnpublishingCollection, setIsUnpublishingCollection] = useState<boolean>(false);
  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    pageSize: constants.MY_COLLECTION_DEFAULT_PAGE_SIZE,
  });

  const renderTitle = (collection: Collection) => {
    return (
      <Link variant="secondary" href={`${COLLECTION_VIEWER_PREFIX}/${collection.collectionId}`}>
        {collection.title}
      </Link>
    );
  };

  const renderState = (collection: Collection) => {
    const { isPublished } = collection;
    if (isPublished) {
      return <StatusIndicator>{constants.MY_COLLECTION_COL_PUBLISHED_TEXT}</StatusIndicator>;
    }
    return (
      <StatusIndicator type={'in-progress'}>
        {constants.MY_COLLECTION_COL_DRAFT_TEXT}
      </StatusIndicator>
    );
  };

  const fetchCollections = useCallback(async () => {
    setIsLoading(true);
    const response = await mplServiceClient.listAllCollections();
    setCollections(
      response.collections.map((collection) => ({
        ...collection,
        // @ts-expect-error TS18048 - strict migration
        ownerNamesString: collection.owners.map((owner) => owner.name).join(', '),
      })),
    );
    setIsLoading(false);
  }, []);

  useEffect(() => {
    fetchCollections();
  }, [fetchCollections]);

  const {
    items,
    actions,
    collectionProps,
    filteredItemsCount,
    paginationProps,
    propertyFilterProps,
  } = useCollection(collections, {
    sorting: {},
    selection: {},
    propertyFiltering: {
      filteringProperties: COLLECTION_FILTERING_PROPERTIES,
      empty: <TableEmptyState />,
      noMatch: (
        <TableNoMatchState
          onClearFilter={() => {
            actions.setPropertyFiltering({ tokens: [], operation: 'and' });
          }}
        />
      ),
    },
    pagination: { pageSize: preferences.pageSize },
  });

  const columnDefinitions = [
    {
      id: constants.ADMIN_COLLECTION_COL_COLLECTION_ID,
      header: constants.ADMIN_COLLECTION_COL_COLLECTION_ID,
      // @ts-expect-error TS7006 - strict migration
      cell: (collection) => collection.collectionId,
      sortingField: 'collectionId',
    },
    {
      id: constants.MY_COLLECTION_COL_TITLE,
      header: constants.MY_COLLECTION_COL_TITLE,
      // @ts-expect-error TS7006 - strict migration
      cell: (collection) => renderTitle(collection),
      sortingField: 'title',
    },
    {
      id: constants.MY_COLLECTION_COL_OWNERS,
      header: constants.MY_COLLECTION_COL_OWNERS,
      // @ts-expect-error TS7006 - strict migration
      cell: (collection) => (
        // @ts-expect-error TS2322 - strict migration
        <CollectionOwners owners={collection.owners} userProfile={userProfile} />
      ),
      sortingField: 'owners',
    },
    {
      id: constants.ADMIN_COLLECTION_COL_LAST_MODIFIED,
      header: constants.ADMIN_COLLECTION_COL_LAST_MODIFIED,
      // @ts-expect-error TS7006 - strict migration
      cell: (collection) => collection.lastModified.toLocaleString(DateTime.DATE_MED),
      sortingField: 'lastModified',
    },
    {
      id: constants.ADMIN_COLLECTION_COL_AUDIENCE,
      header: constants.ADMIN_COLLECTION_COL_AUDIENCE,
      // @ts-expect-error TS7006 - strict migration
      cell: (collection) => collectionAudienceLabelMap.get(collection.audience),
      sortingField: 'audience',
    },
    {
      id: constants.ADMIN_COLLECTION_COL_STATE,
      header: constants.ADMIN_COLLECTION_COL_STATE,
      // @ts-expect-error TS7006 - strict migration
      cell: (collection) => renderState(collection),
      sortingField: 'isPublished',
    },
    {
      id: constants.ADMIN_COLLECTION_COL_OFFICIAL_OFFERING_ID,
      header: constants.ADMIN_COLLECTION_COL_OFFICIAL_OFFERING_ID,
      // @ts-expect-error TS7006 - strict migration
      cell: (collection) => (collection.isOfficial ? 'Yes' : 'No'),
      sortingField: 'isOfficial',
      editConfig: {
        ariaLabel: constants.ADMIN_COLLECTION_COL_OFFICIAL_OFFERING_ID,
        editIconAriaLabel: 'editable',
        // @ts-expect-error TS7006 - strict migration
        editingCell: (item, { currentValue, setValue }) => {
          const value =
            (currentValue ?? item.isOfficial)
              ? constants.ADMIN_COLLECTION_COL_OFFICIAL_OFFERING_ID
              : constants.ADMIN_COLLECTION_COL_UNOFFICIAL_OFFERING_ID;
          return (
            <Select
              autoFocus
              expandToViewport
              selectedOption={setOfficialOptions.find((option) => option.value === value) ?? null}
              onChange={(event) => {
                setValue(event.detail.selectedOption.value ?? item.isOfficial);
              }}
              options={setOfficialOptions}
              disabled={!item.isPublished}
              ariaRequired
            />
          );
        },
      },
    },
  ];

  const onSelectionChange = (selectedItems: AdminCollectionTableItem[]) => {
    setSelectedItem(selectedItems[0]);
  };

  const handleUnPublishCollection = async () => {
    if (selectedItem) {
      setIsUnpublishingCollection(true);
      await mplServiceClient.unpublishCollection(selectedItem.collectionId);
      setIsUnpublishingCollection(false);
      await fetchCollections();
    }
  };

  const handleManageOwners = () => {
    setShowEditOwnersModal(true);
  };

  const actionControls = (
    <SpaceBetween size="s" direction="horizontal">
      <Button
        disabled={!selectedItem || !selectedItem.isPublished}
        onClick={handleUnPublishCollection}
        loading={isUnpublishingCollection}
      >
        Unpublish collection
      </Button>
      <Button disabled={!selectedItem} onClick={handleManageOwners}>
        Edit owners
      </Button>
      <Button
        iconName="refresh"
        variant="icon"
        ariaLabel="Refresh collections"
        onClick={async () => await fetchCollections()}
      />
    </SpaceBetween>
  );

  const header = (
    <Header variant="h3" counter={`(${collections.length})`} actions={actionControls}>
      Collections
    </Header>
  );

  const handleSetOwners = (updatedOwners: Owner[]) => {
    if (selectedItem?.owners) {
      selectedItem.owners = updatedOwners;
      selectedItem.ownerNamesString = updatedOwners.map((owner) => owner.name).join(', ');
      setSelectedItem({ ...selectedItem });
    }
  };

  const handleSubmitEdit = async (
    item: Collection,
    column: TableProps.ColumnDefinition<Collection>,
    newValue: string,
  ) => {
    const { collectionId } = item;
    if (column.id === constants.ADMIN_COLLECTION_COL_OFFICIAL_OFFERING_ID) {
      const type =
        newValue === constants.ADMIN_COLLECTION_COL_OFFICIAL_OFFERING_ID
          ? CollectionEventType.SET_OFFICIAL
          : CollectionEventType.UNSET_OFFICIAL;
      await mplServiceClient.createCollectionEvent(type, collectionId);
      const updatedCollections = collections.map((collection) => {
        if (collection.collectionId === item.collectionId) {
          return {
            ...collection,
            isOfficial:
              newValue === constants.ADMIN_COLLECTION_COL_OFFICIAL_OFFERING_ID ? true : false,
          };
        } else {
          return collection;
        }
      });
      setCollections(updatedCollections);
    }
  };

  return (
    <div>
      <Table
        {...collectionProps}
        loading={!!isLoading}
        loadingText={'Loading collections'}
        header={header}
        items={items}
        columnDefinitions={columnDefinitions}
        resizableColumns
        selectionType="single"
        selectedItems={selectedItem ? [selectedItem] : []}
        onSelectionChange={({ detail }) => onSelectionChange(detail.selectedItems)}
        // @ts-expect-error TS2322 - strict migration
        submitEdit={handleSubmitEdit}
        filter={
          <PropertyFilter
            {...propertyFilterProps}
            i18nStrings={DefaultI18nStrings}
            countText={getFilterCounterText(filteredItemsCount)}
            expandToViewport
            filteringPlaceholder="Filter collections by property or value"
          />
        }
        preferences={
          <Preferences disabled={false} preferences={preferences} setPreferences={setPreferences} />
        }
        pagination={<Pagination {...paginationProps} ariaLabels={tablePaginationAriaLabels} />}
        ariaLabels={{ tableLabel: 'Collections table' }}
      />
      <Modal
        onDismiss={() => setShowEditOwnersModal(false)}
        visible={showEditOwnersModal}
        size="large"
        header="Edit collection owners"
        closeAriaLabel="Close modal"
      >
        <EditCollectionOwners
          owners={selectedItem?.owners ?? []}
          collectionId={selectedItem?.collectionId ?? ''}
          setOwners={handleSetOwners}
          isAdminTable
        />
      </Modal>
    </div>
  );
};

export default AdminCollectionsTable;
